import { useState } from "react";
import useSWR from "swr";
import { Button, Card, InputNumber, Typography } from "antd";
import useCart from "grandus-lib/hooks/useCart";
import useWebInstance from "grandus-lib/hooks/useWebInstance";
import { isEmpty, get, toNumber, find } from "lodash";

import dynamic from "next/dynamic";
const LoginForm = dynamic(() => import("components/forms/Login"));
const RegisterForm = dynamic(() => import("components/forms/Register"));

import styles from "./Credits.module.scss";
import useUser from "grandus-lib/hooks/useUser";

const { Text } = Typography;

const authSectionContentList = {
  login: <LoginForm showRegister={false} />,
  register: <RegisterForm />,
};

const Feedback = ({ touched, status, message }) => {
  if (touched && message && status) {
    return <Text type={status}>{message}</Text>;
  }
  return null;
};

const Credits = (props) => {
  const { data: staticBlocks, isValidating } = useSWR(
    `/api/lib/v1/blocks`,
    (url) => fetch(url).then((r) => r.json()),
    {
      revalidateOnReconnect: false,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    }
  );

  const { cart, mutateCart, isLoading, applyCredits } = useCart();
  const { user } = useUser();
  const { settings } = useWebInstance();
  const [authSectionVisible, toggleAuthSection] = React.useState(false);
  const [authSectionActiveTab, toggleAuthSectionActiveTab] = React.useState(
    "login"
  );

  const fieldName = "credits";
  const cartCredit = get(cart, "credit");
  const cartHasCredit = !isEmpty(cartCredit);
  const [touched, setTouched] = useState(cartHasCredit);
  const [value, setValue] = useState(cartCredit);
  const [validateStatus, setValidateStatus] = useState(
    cartHasCredit ? "success" : ""
  );
  const [message, setMessage] = useState();

  const handleChange = async (value) => {
    setValue(value);
    setTouched(true);
    setMessage(null);
  };

  const handleBlur = (e) => {
    setValue(e.target.value);
    setTouched(true);
  };

  const onApplyCreditsClick = async (e) => {
    e.preventDefault();
    setTouched(true);
    setMessage(null);
    setValidateStatus(null);

    if (toNumber(value) < 0) {
      setValidateStatus("danger");
      setMessage("Hodnota kupónu musí byť väčšia alebo rovná 0");
    }

    const response = await applyCredits(value);
    if (response) {
      if (!get(response, "status", true)) {
        setValidateStatus("danger");
      } else {
        setValidateStatus("success");
        mutateCart(get(response, "data"), false);
      }
      setMessage(get(response, "message", ""));
    }
  };

  if (!toNumber(settings?.credits_allow_user)) {
    return null;
  }

  const cartCreditDescription = get(
    find(staticBlocks, ["hash", "cart_credit_description"]),
    "content",
    ""
  );
  return (
    <Card
      title={"Kredity"}
      bodyStyle={{ padding: !user && authSectionVisible ? "1px 0" : "24px" }}
    >
      {user ? (
        <Card.Meta
          description={
            <CreditsContent
              cartCreditDescription={cartCreditDescription}
              input={
                <>
                  <InputNumber
                    label="Vložiť počet kreditov"
                    min={0}
                    max={get(user, "credit", 0)}
                    defaultValue={0}
                    name={fieldName}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={styles?.input}
                  />{" "}
                  z {user?.credit}
                </>
              }
              button={
                <Button
                  type="primary"
                  onClick={onApplyCreditsClick}
                  disabled={!value || isLoading}
                  loading={isLoading}
                >
                  Použiť
                </Button>
              }
              feedback={
                <Feedback
                  touched={touched}
                  status={validateStatus}
                  message={message}
                />
              }
            />
          }
        />
      ) : (
        <>
          {authSectionVisible ? (
            <Card
              style={{ width: "100%" }}
              bordered={false}
              tabList={[
                {
                  key: "login",
                  tab: "Prihlásenie",
                },
                {
                  key: "register",
                  tab: "Registrácia",
                },
              ]}
              activeTabKey={authSectionActiveTab}
              onTabChange={(key) => {
                toggleAuthSectionActiveTab(key);
              }}
            >
              {authSectionContentList[authSectionActiveTab]}
            </Card>
          ) : (
            <>
              Pre uplatnenie kreditov sa musíte{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  toggleAuthSection(!authSectionVisible);
                }}
              >
                prihlásiť alebo registrovať
              </a>
            </>
          )}
        </>
      )}
    </Card>
  );
};

const CreditsContent = ({ input, button, feedback, cartCreditDescription }) => {
  return (
    <>
      {!isEmpty(cartCreditDescription) ? (
        <div dangerouslySetInnerHTML={{ __html: cartCreditDescription }} />
      ) : (
        <div>
          Tu môžete aplikovať kredity, ktoré ste nazbierali. Na základe množstva aplikovaných kreditov sa Vám z celkovej ceny odráta čiastka uhradená kreditmi.
        </div>
      )}
      
      <div
        className={styles.creditsWrapper}
        style={{ display: "flex", alignItems: "center" }}
      >
        {input} {button}
      </div>
      <div className={styles?.feedback}>{feedback}</div>
    </>
  );
};

export default Credits;
